// admin-ui/src/components/StatusCard.tsx
// ALL RIGHTS RESERVED

/**
 * System Status Card
 * Shows circuit breaker state with visual indicator
 */

import { SystemStatus } from '../api/client';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

interface StatusCardProps {
  status: SystemStatus;
}

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

function getStatusColor(state: SystemStatus['status']): string {
  switch (state) {
    case 'NORMAL': return '#22C55E';      // Green
    case 'DELAYED': return '#F59E0B';     // Yellow
    case 'BANK_DEGRADED': return '#F97316'; // Orange
    case 'CRITICAL': return '#EF4444';    // Red
    default: return '#6B7280';            // Gray
  }
}

function getStatusLabel(state: SystemStatus['status']): string {
  switch (state) {
    case 'NORMAL': return 'Normal';
    case 'DELAYED': return 'Delayed';
    case 'BANK_DEGRADED': return 'Bank Degraded';
    case 'CRITICAL': return 'Critical';
    default: return 'Unknown';
  }
}

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export function StatusCard({ status }: StatusCardProps) {
  const color = getStatusColor(status.status);
  const label = getStatusLabel(status.status);
  const isNormal = status.status === 'NORMAL';

  return (
    <div 
      className={`status-card ${status.status.toLowerCase()}`}
      style={{ borderLeftColor: color }}
    >
      <div className="status-indicator">
        <div 
          className="status-dot"
          style={{ backgroundColor: color }}
        />
        <span className="status-label">{label}</span>
      </div>
      
      <div className="status-details">
        <div className="status-message">{status.message}</div>
        
        <div className="status-metrics">
          <div className="metric">
            <label>Queue Depth</label>
            <span>{status.queueDepth}</span>
          </div>
          <div className="metric">
            <label>Thresholds</label>
            <span>
              {status.thresholds.delayed} / {status.thresholds.bankDegraded} / {status.thresholds.critical}
            </span>
          </div>
          {status.disabledActions.length > 0 && (
            <div className="metric disabled-actions">
              <label>Disabled Actions</label>
              <span>{status.disabledActions.join(', ')}</span>
            </div>
          )}
        </div>
      </div>
      
      {!isNormal && (
        <div className="status-alert">
          ⚠️ System operating in degraded mode
        </div>
      )}
    </div>
  );
}

export default StatusCard;
